import type { CarePlanCarryover } from '../types/patient';
import type { BaselineReading } from './ehrChanges';

/**
 * PHA-1157: the status shown above a regenerated draft, saying where its content came from.
 * Condition and goal names only - never item text, and no item counts (they read as noise:
 * "36 items changed?").
 *
 * `carryover` is undefined when the draft was RESUMED (the generate response that carried the
 * summary is gone) - then we cannot say what it started from, only offer the reset.
 */
export function describeCarryover(carryover: CarePlanCarryover | undefined): string {
  if (!carryover) {
    return 'You can discard the edits in this draft and start over from the standard guidelines.';
  }
  if (!carryover.applied) {
    return 'This draft was generated from the standard guidelines.';
  }
  const parts = ['This draft starts from the previous approved care plan, so its edits are kept.'];
  const added = carryover.added_from_guidelines;
  const addedCount = added ? added.goals + added.tasks + added.questions : 0;
  const reconciled = carryover.reconciled_with_previous;
  const reconciledCount = reconciled ? reconciled.goals + reconciled.tasks + reconciled.questions : 0;
  if (carryover.new_conditions?.length) {
    parts.push(
      `Standard-guideline content was added for ${carryover.new_conditions.join(', ')}` +
        (reconciledCount > 0 ? '; where it repeated one of your items, your version was kept.' : '.')
    );
  }
  if (carryover.removed_conditions?.length) {
    // Only items tagged with a condition can be dropped with it; untagged ones are kept as
    // general, so do not claim a removal that did not happen.
    const dropped = carryover.dropped_for_unselected_conditions;
    const droppedCount = dropped ? dropped.goals + dropped.tasks + dropped.questions : 0;
    const names = carryover.removed_conditions.join(', ');
    parts.push(
      droppedCount > 0
        ? `Content for ${names} was removed because it is no longer selected; check for any of its items that remain.`
        : `${names} is no longer selected; remove any of its items that remain.`
    );
  }
  const updated = (carryover.target_updates || []).map((u) => u.measure).filter(Boolean);
  if (updated.length) {
    parts.push(
      `Newer readings give a different ${updated.join(' and ')} target; your previous target is kept and the recalculated one is shown on the Goals tab.`
    );
  }
  if (addedCount > 0 && carryover.patient_friendly_plan === 'generated') {
    parts.push('The plain-English plan was regenerated; open the print version to check that it matches your edits.');
  }
  return parts.join(' ');
}

/** A BP / BMI target recalculated from a newer reading that the draft did NOT adopt. */
export interface RecalculatedTarget {
  measure: string;
  target: string | number | null;
  readings: Record<string, BaselineReading>;
}

const normalize = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^0-9a-z]+/g, ' ')
    .trim();

/**
 * PHA-1157: the recalculated target for a goal, read from the draft's target baseline (where
 * the server keeps it under `recalculated_targets`, so a resumed draft still offers it).
 */
export function recalculatedTargetFor(
  measure: string | null | undefined,
  baseline: Record<string, unknown> | null | undefined
): RecalculatedTarget | null {
  const entries = (baseline as { recalculated_targets?: unknown } | null | undefined)?.recalculated_targets;
  if (!Array.isArray(entries) || !measure) return null;
  const key = normalize(measure);
  return (
    (entries as RecalculatedTarget[]).find(
      (e) => e && normalize(e.measure) === key && e.target !== null && e.target !== undefined
    ) || null
  );
}

/** Targets compared the way the server compares them: case and whitespace ignored. */
export function sameTarget(a: unknown, b: unknown): boolean {
  const squash = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, '');
  return squash(a) === squash(b);
}
